import { Fragment } from 'react'
import { Plus, MoreVertical, MoveRight, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { VerticalTab } from '@/components/panel'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { ComponentType } from '@/types/schemas/workspace'
import type { WindowConfig } from '@/workspace/types'
import { COMPONENT_META } from './add-panel-menu-constants'
import { AddComponentMenuItems } from './add-panel-menu'

interface PaneTabStripProps {
  /** Ordered tab panel IDs (the host leaf plus any extra tab panels). */
  tabIds: string[]
  /** Currently active tab panel ID. */
  activeTabId: string
  /** The pane's mosaic-leaf host ID. The host tab's kebab offers Move only: closing it
   *  would leave the pane without its leaf. */
  hostId: string
  /** Window configs, used to resolve each tab's component label. */
  windowConfigs: Record<string, WindowConfig>
  /** Called when a tab is clicked. */
  onSelect: (tabId: string) => void
  /** Called to close a (non-host) tab. */
  onClose?: (tabId: string) => void
  /** Called to move a tab (host included) — enters placement mode. */
  onMoveTab?: (tabId: string) => void
  /** Called to add a new component as a tab. */
  onAddTab?: (componentType: ComponentType) => void
  /** Component types offered in the add menu. */
  availableTypes?: ComponentType[]
}

/**
 * Right-edge vertical tab strip for a pane that hosts multiple components as
 * tabs. Reuses the original VerticalTab accent design (matching the prior
 * Key Stats sidebar). Switching/moving/closing/adding are driven by the store
 * via the provided callbacks. The host tab (the mosaic leaf) can be moved but
 * not closed; closing all extra tabs collapses the group back to a plain pane.
 */
export function PaneTabStrip({
  tabIds,
  activeTabId,
  hostId,
  windowConfigs,
  onSelect,
  onClose,
  onMoveTab,
  onAddTab,
  availableTypes,
}: PaneTabStripProps) {
  // The add-tab menu enforces the same per-type and per-plan caps as the
  // header menu, which means it needs the layout's counts. They come from the
  // configs this strip already receives, so no new prop and no store read.
  const counts: Partial<Record<ComponentType, number>> = {}
  for (const cfg of Object.values(windowConfigs)) {
    counts[cfg.componentType] = (counts[cfg.componentType] ?? 0) + 1
  }
  const panelCounts = counts

  return (
    <div
      className="flex flex-col bg-muted shrink-0 p-0.5 pt-2 border-l border-border"
      role="tablist"
      aria-orientation="vertical"
    >
      {tabIds.map((tabId, i) => {
        const componentType = windowConfigs[tabId]?.componentType
        const label = componentType ? (COMPONENT_META[componentType]?.label ?? 'Panel') : 'Panel'
        // The host tab is the mosaic leaf: it can move (the next tab takes over
        // the pane) but not close on its own.
        const canClose = tabId !== hostId && onClose != null
        const hasMenu = onMoveTab != null || canClose
        const isActive = tabId === activeTabId
        // Light divider between consecutive NON-active tabs — mirrors the
        // top-bar separator (`h-4 w-px bg-border`), rotated for the vertical
        // strip. The slot is ALWAYS reserved for non-first tabs so switching
        // the active tab never shifts the others; only the line's visibility
        // toggles (transparent next to the active tab, whose accent fill is
        // its own boundary).
        const showLine = i > 0 && !isActive && tabIds[i - 1] !== activeTabId
        return (
          <Fragment key={tabId}>
            {i > 0 && (
              <div
                aria-hidden
                className={cn('mx-auto my-0.5 h-px w-4', showLine ? 'bg-border' : 'bg-transparent')}
              />
            )}
            <div className="group/tab relative flex">
              <VerticalTab
                label={label}
                active={isActive}
                onClick={() => onSelect(tabId)}
                // Pad the bottom so every tab reserves the kebab's space — keeps
                // all tabs the same length, including the host tab (no kebab).
                className="w-full pb-6"
              />
              {hasMenu && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      onClick={(e) => e.stopPropagation()}
                      className="invisible absolute bottom-1 left-1/2 flex h-4 w-4 -translate-x-1/2 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-hover hover:text-foreground group-hover/tab:visible data-[state=open]:visible"
                      aria-label={`${label} tab options`}
                    >
                      <MoreVertical className="h-3 w-3" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent side="left" align="start">
                    {onMoveTab && (
                      <DropdownMenuItem onClick={() => onMoveTab(tabId)}>
                        <MoveRight className="mr-2 h-4 w-4" />
                        Move
                      </DropdownMenuItem>
                    )}
                    {canClose && (
                      <DropdownMenuItem onClick={() => onClose?.(tabId)}>
                        <X className="mr-2 h-4 w-4" />
                        Close
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </Fragment>
        )
      })}
      {onAddTab && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="mt-px flex items-center justify-center rounded p-1 text-muted-foreground transition-colors hover:bg-hover hover:text-foreground"
              aria-label="Add tab"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="left" align="start">
            <AddComponentMenuItems
              availableTypes={availableTypes}
              onPick={onAddTab}
              panelCounts={panelCounts}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}
